export const PricingService = {
    isPromoValide: function(article, prefix) {
        if (!article) return false;
        
        const getRaw = (key) => {
            if (article[key] !== undefined) return article[key];
            const lowerKey = key.toLowerCase();
            if (article[lowerKey] !== undefined) return article[lowerKey];
            const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
            return article[snakeKey];
        };

        const isPromoCoche = Number(getRaw(`${prefix}IsPromo`) || 0);
        if (isPromoCoche === 0) return true;

        const debut = getRaw(`${prefix}DateDebut`);
        const fin = getRaw(`${prefix}DateFin`) || getRaw(`${prefix}DateFIn`);

        if (!debut || !fin) return false;

        const maintenant = new Date();
        const dDebut = new Date(debut);
        const dFin = new Date(fin);

        return maintenant >= dDebut && maintenant <= dFin;
    },

    calculerPrixPaliersLots: function(paliers, qteSaisie, prixVenteBase) {
        // Normalisation de la quantité saisie à 4 décimales pour comparaison précise
        let qteRestante = Number(Number(qteSaisie || 0).toFixed(4));
        let montantTotalLotsTTC = 0;

        if (!paliers || paliers.length === 0) {
            return qteRestante * prixVenteBase;
        }

        const paliersTries = [...paliers]
            .map(p => ({
                quantite: Number(Number(p.quantite || p.QUANTITE || 0).toFixed(4)),
                prix_total: Number(p.prix_total || p.PRIX_TOTAL || 0)
            }))
            .filter(p => p.quantite > 0 && p.prix_total > 0)
            .sort((a, b) => b.quantite - a.quantite);

        if (paliersTries.length === 0) {
            return qteRestante * prixVenteBase;
        }

        // 1. Application des paliers par lots décroissants avec tolérance à 4 décimales
        for (const palier of paliersTries) {
            const qteArrondie = Number(qteRestante.toFixed(4));
            const palierArrondi = Number(palier.quantite.toFixed(4));

            if (qteArrondie >= palierArrondi) {
                const nbLots = Math.floor(qteArrondie / palierArrondi);
                montantTotalLotsTTC += nbLots * palier.prix_total;
                qteRestante = Number((qteArrondie % palierArrondi).toFixed(4));
            }
        }

        // 2. Traitement du reliquat résiduel
        if (qteRestante > 0) {
            const plusPetitPalier = paliersTries[paliersTries.length - 1];
            const qteRestanteArrondie = Number(qteRestante.toFixed(4));
            const plusPetitPalierArrondi = Number(plusPetitPalier.quantite.toFixed(4));

            // Si les 4 derniers chiffres après la virgule correspondent pile au palier (ex: 0.0667)
            if (qteRestanteArrondie === plusPetitPalierArrondi) {
                montantTotalLotsTTC += plusPetitPalier.prix_total;
            } else if (qteRestanteArrondie <= plusPetitPalierArrondi && plusPetitPalier.quantite < 1) {
                // Prorata exact basé sur le palier unitaire défini
                const prixUnitaireDetail = plusPetitPalier.prix_total / plusPetitPalier.quantite;
                montantTotalLotsTTC += qteRestanteArrondie * prixUnitaireDetail;
            } else {
                montantTotalLotsTTC += qteRestanteArrondie * prixVenteBase;
            }
        }

        return Number(montantTotalLotsTTC.toFixed(4));
    },

    calculerLigne: function(article, qteSaisie) {
        const qte = Number(String(qteSaisie || 0).replace(',', '.')) || 0;
        
        const getVal = (key) => {
            if (!article) return 0;
            if (article[key] !== undefined) return Number(article[key]);
            const lowerKey = key.toLowerCase();
            if (article[lowerKey] !== undefined) return Number(article[lowerKey]);
            const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
            if (article[snakeKey] !== undefined) return Number(article[snakeKey]);
            return 0;
        };
        
        const pVenteTTCBase = getVal('prixVente');
        
        // --- ⚡ ÉTAPE 1 : PALIERS TTC ---
        let montantBrutTTCApresPaliers = pVenteTTCBase * qte;
        
        const paliersData = article?.paliers || article?.productPaliers || [];
        if (getVal('palierActive') === 1 || getVal('palier_active') === 1 || (Array.isArray(paliersData) && paliersData.length > 0)) {
            montantBrutTTCApresPaliers = this.calculerPrixPaliersLots(paliersData, qte, pVenteTTCBase);
        }

        const taxeTaux = getVal('taxeActive') === 1 ? getVal('taxeTaux') : 0;
        const t = taxeTaux / 100;

        let remiseTotaleTTC = 0;
        const typesAppliques = [];

        if (getVal('remiseActive') === 1 || getVal('remise_active') === 1) {
            // --- R1 : UNITAIRE PONDÉRÉ / PROPORTIONNEL STRICT (qte * (r1Montant / r1Seuil)) ---
            if (getVal('r1Active') === 1 && this.isPromoValide(article, 'r1')) {
                const r1M = getVal('r1Montant'), r1T = getVal('r1Taux'), seuilR1 = getVal('r1Seuil') > 0 ? getVal('r1Seuil') : 1;
                if (qte >= seuilR1) {
                    const valR1 = r1M > 0 ? qte * (r1M / seuilR1) : montantBrutTTCApresPaliers * (r1T / 100);
                    if (valR1 > 0) {
                        remiseTotaleTTC += valR1;
                        typesAppliques.push('R1');
                    }
                }
            }

            // --- R2 : REMISE EN GROS (forfait fixe dès seuil) ---
            if (getVal('r2Active') === 1 && this.isPromoValide(article, 'r2')) {
                const r2M = getVal('r2Montant'), r2T = getVal('r2Taux'), seuilR2 = getVal('r2Seuil') > 0 ? getVal('r2Seuil') : 1;
                if (qte >= seuilR2) {
                    const valR2 = r2M > 0 ? r2M : montantBrutTTCApresPaliers * (r2T / 100);
                    if (valR2 > 0) {
                        remiseTotaleTTC += valR2;
                        typesAppliques.push('R2');
                    }
                }
            }

            // --- R3 : REMISE PAR MULTIPLE ---
            if (getVal('r3Active') === 1 && this.isPromoValide(article, 'r3')) {
                const mult = getVal('r3Multiple');
                if (mult > 0 && qte >= mult) {
                    const nbMultiples = Math.floor(qte / mult);
                    const r3M = getVal('r3Montant'), r3T = getVal('r3Taux');
                    const trancheBrutRef = (montantBrutTTCApresPaliers / qte) * mult;
                    const valR3 = r3M > 0 ? (nbMultiples * r3M) : (nbMultiples * trancheBrutRef * (r3T / 100));
                    if (valR3 > 0) {
                        remiseTotaleTTC += valR3;
                        typesAppliques.push('R3');
                    }
                }
            }

            // --- R4 : INTERVALLE CUMULÉ (Somme des niveaux A, B, C atteints) ---
            if (getVal('r4Active') === 1 && this.isPromoValide(article, 'r4')) {
                let r4Val = 0;
                const aMax = getVal('r4A_Max');
                const bMax = getVal('r4B_Max');
                const aM = getVal('r4A_Montant'), aT = getVal('r4A_Taux');
                const bM = getVal('r4B_Montant'), bT = getVal('r4B_Taux');
                const cM = getVal('r4C_Montant'), cT = getVal('r4C_Taux');

                // Cumul des tranches atteintes si la quantité dépasse les seuils
                if (aMax > 0 && qte >= aMax) {
                    r4Val += aM > 0 ? aM : (montantBrutTTCApresPaliers * (aT / 100));
                }
                if (bMax > 0 && qte >= bMax) {
                    r4Val += bM > 0 ? bM : (montantBrutTTCApresPaliers * (bT / 100));
                }
                if (qte >= 20) {
                    r4Val += cM > 0 ? cM : (montantBrutTTCApresPaliers * (cT / 100));
                }

                if (r4Val > 0) {
                    remiseTotaleTTC += r4Val;
                    typesAppliques.push('R4');
                }
            }
        }

        // Sécurité anti-sur-remise TTC
        remiseTotaleTTC = Math.min(Math.max(0, remiseTotaleTTC), montantBrutTTCApresPaliers);

        // Net TTC après remise
        const montantTTCTotal = Number((montantBrutTTCApresPaliers - remiseTotaleTTC).toFixed(4));
        
        // Rétro-calcul propre de la TVA/HT à partir du net TTC
        const montantHTTotal = Number((montantTTCTotal / (1 + t)).toFixed(4));
        const montantTaxeTotal = Number((montantTTCTotal - montantHTTotal).toFixed(4));
        const htUnitaireMoyen = qte > 0 ? Number((montantHTTotal / qte).toFixed(4)) : 0;

        return {
            montantBrutTTC: Number(montantBrutTTCApresPaliers.toFixed(4)),
            remiseTotale: Number(remiseTotaleTTC.toFixed(4)),
            typeRemise: typesAppliques.length > 0 ? typesAppliques.join('+') : null,
            prixHTUnitaire: htUnitaireMoyen,
            montantHT: montantHTTotal,
            montantTaxe: montantTaxeTotal,
            taxeTaux: taxeTaux,
            netAPayer: montantTTCTotal
        };
    }
};